class ReviewDrawer extends HTMLElement {
  constructor() {
    super();
    this.overlay = this.querySelector('[data-review-overlay]');
    this.panels = {
      list: this.querySelector('[data-panel="list"]'),
      write: this.querySelector('[data-panel="write"]'),
    };
    this.reviewsLoaded = false;
  }

  connectedCallback() {
    // Portal to <body> so position:fixed escapes any transformed/overflow/contain ancestor
    // (the product-info section), which was clipping the overlay + drawer on scroll/resize.
    if (this.parentElement && this.parentElement !== document.body) {
      document.querySelectorAll('body > review-drawer').forEach((el) => { if (el !== this) el.remove(); });
      document.body.appendChild(this);
      return; // re-enters connectedCallback with <body> as parent
    }
    if (this._initialized) return;
    this._initialized = true;

    this._onOverlayClick = () => this.close();
    this._onKeydown = (e) => { if (e.key === 'Escape' && !this.hasAttribute('hidden')) this.close(); };
    this._onDocClick = (e) => {
      const trigger = e.target.closest('[data-review-open]');
      if (!trigger) return;
      e.preventDefault();
      this.open(trigger.getAttribute('data-review-open'));
    };
    this.overlay.addEventListener('click', this._onOverlayClick);
    document.addEventListener('keydown', this._onKeydown);
    document.addEventListener('click', this._onDocClick);
    this.loadBadge();
    this._onDrawerClick = (e) => {
      const chat = e.target.closest('[data-live-chat]');
      if (chat) {
        e.preventDefault();
        this.openLiveChat();
        this.close();
        return;
      }
      if (e.target.closest('[data-review-more]')) {
        if (this._loadingMore) return;
        this._loadingMore = true;
        this.loadReviews(this.currentPage + 1).finally(() => { this._loadingMore = false; });
      }
      const body = e.target.closest('[data-review-body]');
      if (body) body.classList.toggle('is-expanded');
    };
    this.addEventListener('click', this._onDrawerClick);
    this.setupWriteForm();
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this._onKeydown);
    document.removeEventListener('click', this._onDocClick);
    this.removeEventListener('click', this._onDrawerClick);
  }

  open(panel = 'list') {
    this.panels.list.hidden = panel !== 'list';
    this.panels.write.hidden = panel !== 'write';
    this.removeAttribute('hidden');
    document.body.classList.add('review-drawer-open');
    requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-open')));
    if (panel === 'list' && !this.reviewsLoaded) this.loadReviews(1);
    if (panel === 'write' && this.form) {
      const s = this.form.querySelector('[data-form-status]');
      if (s) { s.hidden = true; s.textContent = ''; }
    }
  }

  close() {
    if (!this.classList.contains('is-open')) return;
    this.classList.remove('is-open');
    document.body.classList.remove('review-drawer-open');
    const inner = this.querySelector('.review-drawer__inner:not([hidden])') || this.querySelector('.review-drawer__inner');
    inner.addEventListener('transitionend', () => {
      if (!this.classList.contains('is-open')) this.setAttribute('hidden', '');
    }, { once: true });
  }

  // Yotpo public app key: from the section setting, else auto-discovered from the Yotpo loader on the page.
  get appKey() {
    if (this._appKey !== undefined) return this._appKey;
    const fromLoader = (document.querySelector('script[src*="cdn-widgetsrepository.yotpo.com/v1/loader/"]') || {}).src || '';
    const match = fromLoader.match(/\/loader\/([^/?#]+)/);
    this._appKey = this.dataset.appKey || (match ? match[1] : '');
    return this._appKey;
  }

  // Yotpo public widget reviews API — one call returns bottomline (avg/count) + reviews + pagination.
  async fetchPage(page) {
    const url = `https://api-cdn.yotpo.com/v1/widget/${encodeURIComponent(this.appKey)}`
      + `/products/${encodeURIComponent(this.dataset.productId)}/reviews.json?per_page=5&page=${page}`;
    const res = await fetch(url);
    const json = await res.json();
    return (json && json.response) || {};
  }

  // Returns {avg, count} from the bottomline. Caches the page-1 response for first list render.
  async fetchBadge() {
    if (this._badge) return this._badge;
    try {
      const resp = await this.fetchPage(1);
      this._page1 = resp;
      const bl = resp.bottomline || {};
      this._badge = { avg: bl.average_score || 0, count: bl.total_review || 0 };
    } catch (_) {
      this._badge = { avg: 0, count: 0 };
    }
    return this._badge;
  }

  async loadBadge() {
    const stars = document.querySelector('[data-review-badge-stars]');
    const count = document.querySelector('[data-review-badge-count]');
    if (!count) return;
    const { avg, count: n } = await this.fetchBadge();
    const rounded = Math.round(avg);
    if (stars) stars.textContent = '★★★★★☆☆☆☆☆'.slice(5 - rounded, 10 - rounded);
    count.textContent = `${n} ${n === 1 ? 'review' : 'reviews'}`;
  }

  async loadReviews(page) {
    try {
      let resp;
      if (page === 1 && this._page1) {
        resp = this._page1;
      } else {
        resp = await this.fetchPage(page);
        if (page === 1) this._page1 = resp;
      }
      this.reviewsLoaded = true;
      this.renderReviews(resp, page);
    } catch (err) {
      console.error('[review-drawer] load failed', err);
    }
  }

  renderStars(rating) {
    const full = Math.round(rating);
    return '★★★★★☆☆☆☆☆'.slice(5 - full, 10 - full);
  }

  async renderSummary() {
    const summary = this.querySelector('[data-review-summary]');
    const { avg, count } = await this.fetchBadge();
    summary.innerHTML = `<span class="review-drawer__avg">${avg.toFixed(1)}</span>`
      + `<span class="review-drawer__avg-max"> / 5.0</span>`
      + `<span class="review-drawer__count"> (${count})</span>`;
  }

  static esc(s) {
    return String(s).replace(/[&<>"']/g, (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  renderReviews(resp, page) {
    if (page === 1) this.renderSummary();
    const list = this.querySelector('[data-review-list]');
    const empty = this.querySelector('[data-review-empty]');
    const reviews = resp.reviews || [];
    if (page === 1 && reviews.length === 0) {
      empty.hidden = false;
      this.currentPage = 1;
      this.totalPages = 1;
      this.renderListFooter();
      return;
    }
    empty.hidden = true;
    const html = reviews.map((r) => {
      const date = ReviewDrawer.esc((r.created_at || '').slice(0, 10));
      const name = ReviewDrawer.esc((r.user && r.user.display_name) || r.display_name || 'Anonymous');
      const body = ReviewDrawer.esc(r.content || '');
      return `<article class="review-item">
        <div class="review-item__date">${date}</div>
        <div class="review-item__stars">${this.renderStars(r.score)}</div>
        <p class="review-item__body" data-review-body>${body}</p>
        <div class="review-item__author">${name}</div>
      </article>`;
    }).join('');
    if (page === 1) list.querySelectorAll('.review-item').forEach((n) => n.remove());
    list.insertAdjacentHTML('beforeend', html);
    const pg = resp.pagination || {};
    this.currentPage = pg.page || page;
    this.totalPages = pg.per_page ? Math.ceil((pg.total || 0) / pg.per_page) : 1;
    this.renderListFooter();
  }

  renderListFooter() {
    const footer = this.querySelector('[data-review-list-footer]');
    footer.innerHTML = (this.currentPage < this.totalPages)
      ? '<button type="button" class="review-drawer__more" data-review-more>load more</button>'
      : '';
  }

  // ---- Write form (custom Figma form -> Yotpo public create API) ----

  setupWriteForm() {
    this.form = this.querySelector('[data-review-form]');
    if (!this.form) return;
    this.scoreInput = this.form.querySelector('[data-form-score]');
    this.starButtons = Array.from(this.form.querySelectorAll('.review-form__star'));
    this.starButtons.forEach((btn) => {
      btn.addEventListener('click', () => this.setScore(parseInt(btn.dataset.star, 10)));
    });
    this.form.addEventListener('submit', (e) => { e.preventDefault(); this.submitReview(); });
  }

  setScore(score) {
    this.scoreInput.value = score || '';
    this.starButtons.forEach((b) => b.classList.toggle('is-active', parseInt(b.dataset.star, 10) <= score));
  }

  // Opens Channel Talk. The launcher button lives inside an open shadow DOM (#ch-plugin-entry),
  // so document.querySelector can't reach it — use the official ChannelIO API, with a shadow-DOM
  // click as fallback.
  openLiveChat() {
    if (typeof window.ChannelIO === 'function') {
      window.ChannelIO('showMessenger');
      return;
    }
    const host = document.querySelector('#ch-plugin-entry > div');
    const btn = host && host.shadowRoot && host.shadowRoot.querySelector('[data-ch-testid="launcher"] button');
    if (btn) btn.click();
  }

  async submitReview() {
    const status = this.form.querySelector('[data-form-status]');
    const submit = this.form.querySelector('[data-form-submit]');
    const val = (sel) => (this.form.querySelector(sel)?.value || '').trim();
    const score = parseInt(this.scoreInput.value, 10) || 0;
    const name = val('[data-form-name]');
    const email = val('[data-form-email]');
    const content = val('[data-form-content]');
    const showStatus = (msg, ok) => {
      status.hidden = false;
      status.textContent = msg;
      status.classList.toggle('review-form__status--error', !ok);
      status.classList.toggle('review-form__status--success', ok);
    };
    if (!score) return showStatus('Please select a rating.', false);
    if (!name) return showStatus('Please enter your nickname.', false);
    if (!email) return showStatus('Please enter your email.', false);
    if (content.length < 2) return showStatus('Please write your review.', false);

    submit.disabled = true;
    showStatus('Submitting…', true);
    const payload = new URLSearchParams({
      appkey: this.appKey,
      sku: this.dataset.productId,
      product_title: this.dataset.productTitle || '',
      product_url: this.dataset.productUrl || '',
      display_name: name,
      email,
      review_content: content,
      review_title: content.slice(0, 60) || 'Review',
      review_score: String(score),
    });
    try {
      const res = await fetch('https://api.yotpo.com/v1/widget/reviews', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const data = await res.json().catch(() => ({}));
      const code = (data.status && data.status.code) || data.code || res.status;
      const message = (data.status && data.status.message) || data.message || '';
      if (res.ok && code === 200) {
        this.form.reset();
        this.setScore(0);
        showStatus('Thanks for your review! It will appear after moderation.', true);
        submit.disabled = false;
        clearTimeout(this._closeTimer);
        this._closeTimer = setTimeout(() => this.close(), 3000);
        return;
      }
      if (message === 'duplicate_review') {
        showStatus('You have already reviewed this product.', false);
      } else {
        showStatus(`Something went wrong. Please try again.${message ? ` (${message})` : ''}`, false);
      }
      submit.disabled = false;
    } catch (err) {
      console.error('[review-drawer] submit failed', err);
      showStatus('Network error. Please check your connection and try again.', false);
      submit.disabled = false;
    }
  }
}
customElements.define('review-drawer', ReviewDrawer);
